import { FC, useState, useContext } from "react";
// Utils
import { CommonContext } from "config/commonProvider";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { useAssignAnimalsToGroup, useHasFeature } from "hooks";
// Components
import { Button, GroupsSelect, Modal } from "components";
import { TableOptionsContext } from "config/tableOptionsProvider";
import { TABLE_IDS } from "constants/Interface";

interface AddSelectedAnimalsToGroupProps {
  animalIds: string[];
}

export const AddSelectedAnimalsToGroup: FC<AddSelectedAnimalsToGroupProps> = ({ animalIds }) => {
  const [assignAnimalsToGroup, { loading }] = useAssignAnimalsToGroup();
  const useServerPaginatedAnimalsList = useHasFeature("SERVER_SIDE_PAGINATION_FOR_ANIMALS_LIST");
  const { setTableOptions } = useContext(TableOptionsContext);

  const [showModal, setShowModal] = useState(false);
  const [group, setGroup] = useState("");
  const { showNotification } = useContext(CommonContext);

  const handleModalToggle = (): void => {
    setShowModal(!showModal);
  };

  const handleCancelClick = (event): void => {
    event.stopPropagation();

    handleModalToggle();
  };

  const handleShowError = (errorMessage: string): void => {
    showNotification({
      variant: "error",
      message: errorMessage,
    });

    setShowModal(false);
    setGroup("");
  };

  const handleConfirmClick = async (): Promise<void> => {
    try {
      const { data, errors } = await assignAnimalsToGroup({
        variables: {
          input: {
            animals: animalIds,
            group: group || null,
            date: new Date(),
          },
        },
        refetchQueries: useServerPaginatedAnimalsList ? ["GetAnimalsPage"] : [],
      });

      if (useServerPaginatedAnimalsList) {
        setTableOptions({
          id: TABLE_IDS.ANIMAL_LIST_SERVER,
          selectedRows: [],
          selectedRowsIds: new Set(),
        });
      }

      if (data?.moveAnimalsToGroup?.errors) {
        const errorMessages = data.moveAnimalsToGroup.errors.reduce<string[]>((accumulator, error) => {
          if (error?.message) accumulator.push(error.message);
          return accumulator;
        }, []);

        handleShowError(errorMessages.join("; "));
      } else if (errors) {
        const errorMessages = errors.reduce<string[]>((accumulator, error) => {
          if (error?.message) accumulator.push(error.message);
          return accumulator;
        }, []);

        handleShowError(errorMessages.join("; "));
      } else {
        showNotification({
          message: "Group membership successfully updated",
        });

        setShowModal(false);
      }
    } catch (error) {
      handleShowError("Error updating group membership");
    }
  };

  return (
    <>
      <Button
        id={ELEMENTS_IDS.ANIMALS_ADD_TO_GROUP_BUTTON}
        caption="Add to group"
        onClick={handleModalToggle}
        data-testid="add-to-group-button"
      />
      <Modal
        showCloseButton
        active={showModal}
        handleClose={handleModalToggle}
        title="Add to group"
        subTitle={`Please select a group for these ${animalIds.length} animals. This will change the group membership of all of the selected animals, even if they currently belong to a different group.`}
        actions={{
          secondary: {
            onClick: handleCancelClick,
            caption: "Cancel",
            "data-testid": "add-to-group-cancel-btn",
          },
          primary: {
            onClick: handleConfirmClick,
            caption: "Confirm",
            requesting: loading,
            disabled: loading,
            "data-testid": "add-to-group-confirm-btn",
          },
        }}
      >
        <GroupsSelect value={group} onSelect={setGroup} />
      </Modal>
    </>
  );
};
